import React, { useEffect, useState } from "react";
import api from "constants/api";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { SkewLoader } from "react-spinners";

interface CartItem {
    code: string;
    name: string;
    price: number;
    quantity: number;
    weight: number;
    GST: number;
}

interface Enquiry {
    _id: string;
    order_number?: string;
    odoo_display_name?: string;
    name?: string;
    email?: string;
    mobile?: string;
    message?: string;
    type?: string;
    createdAt?: string;
    cart_items?: CartItem[];
    total_value_after_discount: number;
}

const OrderList: React.FC = () => {
    const [orders, setOrders] = useState<Enquiry[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [color] = useState("#1e3a8a"); // loader color (blue tone)

    const navigate = useNavigate();

    // Fetch all orders
    const getOrderData = async () => {
        setIsLoading(true);
        setError(null);

        const email =
            JSON.parse(localStorage.getItem("AuthCustomerDetails") || "{}").email;
        const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}${api.getEnquiryData}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        email,
                        sort_by: "createdAt",
                        sort_order: "desc",
                    }),
                }
            );

            const result = await response.json();

            if (result.statusCode === 200 && result.data?.result?.length > 0) {
                const paidOrders = result.data.result.filter(
                    (item: Enquiry) =>
                        item.type && item.type.toLowerCase().trim() === "confirm"
                );
                setOrders(paidOrders);
            } else {
                setOrders([]);
            }
        } catch (error) {
            console.error("Failed to fetch order data:", error);
            setError("Failed to fetch orders. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch details of a single enquiry by ID
    const getEnquiry = async (id: string) => {
        try {
            setIsLoading(true);
            const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");
            const apiUrl = `${process.env.REACT_APP_API_URL}${api.inquiryDetail}`;

            const response = await axios.post(
                apiUrl,
                { id },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            navigate(`/orderlist/view/${id}`, {
                state: { enquiry: response.data.data.enquiry },
            });
        } catch (error) {
            console.error("Failed to fetch enquiry details:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getOrderData();
    }, []);

    return (
        <div className="p-6 relative">
            <h2 className="text-2xl font-semibold mb-4 text-center">Orders List</h2>

            {/* ✅ Add SkewLoader overlay */}
            {isLoading && (
                <div className="fixed inset-0 bg-white bg-opacity-75 z-50 flex items-center justify-center">
                    <div className="absolute z-50">
                        <SkewLoader color={color} loading={isLoading} />
                    </div>
                </div>
            )}

            {!isLoading && (
                <>
                    {error ? (
                        <p className="text-red-500">{error}</p>
                    ) : orders.length === 0 ? (
                        <p>No paid orders found.</p>
                    ) : (
                        <div className="overflow-x-auto border rounded-xl shadow-sm">
                            <table className="min-w-full border-collapse">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="p-3 text-left border-b">Quotation No.</th>
                                        <th className="p-3 text-left border-b">Customer Name</th>
                                        <th className="p-3 text-left border-b">Email</th>
                                        <th className="p-3 text-left border-b">Created At</th>
                                        <th className="p-3 text-left border-b">Amount</th>
                                        <th className="p-3 text-center border-b">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((order) => (
                                        <tr key={order._id} className="hover:bg-gray-50">
                                            <td className="p-3 border-b">
                                                {order.odoo_display_name || "-"}
                                            </td>
                                            <td className="p-3 border-b">{order.name || "-"}</td>
                                            <td className="p-3 border-b">{order.email || "-"}</td>
                                            <td className="p-3 border-b">
                                                {order.createdAt
                                                    ? new Date(order.createdAt).toLocaleString()
                                                    : "-"}
                                            </td>
                                            <td className="p-3 border-b">
                                                {order.total_value_after_discount?.toFixed(2) || "-"}
                                            </td>
                                            <td className="p-3 border-b text-center">
                                                <button
                                                    className="text-blue-500 hover:text-blue-700"
                                                    onClick={() => getEnquiry(order._id)}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={1.5}
                                                        stroke="currentColor"
                                                        className="size-6"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                                                        />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default OrderList;
